import * as THREE from 'three';
import { modelLoader } from '../ktx2';

/**
 * The generic model library (docs/research/MESHY-WORLD-PLAN.md, P2): Meshy world props come in packs built by
 * scripts/build-packs.mjs —
 *   - `street`  (public/models/street-pack.glb): every street prop on one shared atlas, merged into the city
 *     chunks at boot (streetModels.ts) or drawn as culled instances (the dustbins, src/props);
 *   - `<scene>` (e.g. `hideout`): the furniture of one interior, a texture each, loaded on entry and released
 *     on exit.
 * A pack holds one geometry per model and LOD (mesh "<id>:<lod>" in the file, with `extras` { id, lod, tris,
 * height, radius, size, glow }) and the texture(s) they sample.
 *
 * The shared glTF loader (render/ktx2.ts: meshopt, KTX2). Nothing here throws or blocks: a pack that fails to load,
 * or is not there at all, resolves to null and every caller keeps its procedural model.
 */

export interface PackModel {
  readonly id: string;
  readonly lod: string;
  /** In the fitted space of the pack: ground at y = 0, front +Z, metres. Attributes: position, normal, uv, color (r = tint weight). */
  readonly geometry: THREE.BufferGeometry;
  /** The texture the model's uvs sample (the street atlas for every street model). */
  readonly texture: THREE.Texture | null;
  readonly tris: number;
  readonly height: number;
  /** Footprint radius about the pivot (m). */
  readonly radius: number;
  /** Has an emissive mask in the texture's alpha (255 = unlit … 128 = fully lit). */
  readonly glow: boolean;
}

export interface ModelPack {
  readonly name: string;
  /** Every texture of the pack (the street pack has one: the atlas). */
  readonly textures: readonly THREE.Texture[];
  /** A model at a LOD (the first LOD the pack has when `lod` is not there), or null. */
  get(id: string, lod?: string): PackModel | null;
  ids(): string[];
  /** Approximate GPU bytes of the pack's textures (with mips). */
  textureBytes(): number;
  dispose(): void;
}

const BASE = import.meta.env?.BASE_URL ?? './';

/**
 * `?classicprops`: no Meshy world packs and no prop culling — the game as it was before them, for A/B
 * screenshots and perf read-outs in the same build (docs/PERF.md).
 */
export const CLASSIC_PROPS = typeof location !== 'undefined' && new URLSearchParams(location.search).has('classicprops');

const loading = new Map<string, Promise<ModelPack | null>>();
const loaded = new Map<string, ModelPack>();

/** The pack if it has finished loading (sync), else null. */
export function modelPack(name: string): ModelPack | null {
  return loaded.get(name) ?? null;
}

/**
 * Load a pack once (cached). Resolves to null when there is no browser, the file is missing or broken, or it
 * takes longer than `timeoutMs` (the load keeps going and the pack is there for whoever asks later).
 */
export function loadModelPack(name: string, timeoutMs = 0, onProgress?: (fraction: number) => void): Promise<ModelPack | null> {
  if (typeof document === 'undefined' || CLASSIC_PROPS) return Promise.resolve(null);
  let p = loading.get(name);
  if (!p) {
    const loader = modelLoader();
    // `onProgress` (the loading bar) hears the download, 0..1, when the server sends a length.
    p = loader
      .loadAsync(`${BASE}models/${name}-pack.glb`, (e) => {
        if (onProgress && e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      })
      .then((gltf) => {
        const pack = parsePack(name, gltf.scene);
        loaded.set(name, pack);
        return pack;
      })
      .catch(() => null);
    loading.set(name, p);
  }
  if (!timeoutMs) return p;
  return Promise.race([p, new Promise<null>((r) => setTimeout(() => r(null), timeoutMs))]);
}

/** Dispose a pack's geometry and textures and forget it (an interior on exit). */
export function releaseModelPack(name: string): void {
  loaded.get(name)?.dispose();
  loaded.delete(name);
  loading.delete(name);
}

/** Build the pack from a loaded glTF scene (exported for tests). */
export function parsePack(name: string, scene: THREE.Object3D): ModelPack {
  const models = new Map<string, PackModel>();
  const firstLod = new Map<string, PackModel>();
  const textures = new Set<THREE.Texture>();
  const materials = new Set<THREE.Material>();
  scene.updateMatrixWorld(true);
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const x = mesh.userData as { id?: string; lod?: string; tris?: number; height?: number; radius?: number; glow?: boolean };
    const [nid, nlod] = mesh.name.split(':');
    const id = x.id ?? nid ?? mesh.name;
    const lod = x.lod ?? nlod ?? 'high';
    const mat = mesh.material as THREE.MeshStandardMaterial;
    materials.add(mat);
    const texture = mat.map ?? null;
    if (texture) textures.add(texture);
    const geometry = mesh.geometry;
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    const m: PackModel = {
      id,
      lod,
      geometry,
      texture,
      tris: x.tris ?? (geometry.index ? geometry.index.count / 3 : 0),
      height: x.height ?? geometry.boundingBox!.max.y,
      radius: x.radius ?? geometry.boundingSphere!.radius,
      glow: !!x.glow,
    };
    models.set(`${id}:${lod}`, m);
    if (!firstLod.has(id)) firstLod.set(id, m);
  });
  return {
    name,
    textures: [...textures],
    get: (id, lod) => (lod ? models.get(`${id}:${lod}`) : undefined) ?? firstLod.get(id) ?? null,
    ids: () => [...firstLod.keys()],
    textureBytes() {
      let b = 0;
      for (const t of textures) {
        // KTX2 (render/ktx2.ts): what the GPU holds is the transcoded mip chain.
        const mips = (t as THREE.CompressedTexture).isCompressedTexture ? (t.mipmaps as { data: ArrayBufferView }[]) : null;
        if (mips?.length) for (const m of mips) b += m.data.byteLength;
        else {
          const img = t.image as { width?: number; height?: number } | null;
          b += (img?.width ?? 0) * (img?.height ?? 0) * 4 * (4 / 3);
        }
      }
      return b;
    },
    dispose() {
      for (const m of models.values()) m.geometry.dispose();
      for (const t of textures) t.dispose();
      for (const m of materials) m.dispose();
    },
  };
}

/** Every pack loaded so far (perf read-outs: `__bh.renderBudget().models`). */
export function loadedModelPacks(): ModelPack[] {
  return [...loaded.values()];
}
